import api from './api.service';

type ApiBody<T> = { success?: boolean; data?: T; message?: string };

async function unwrap<T>(promise: Promise<unknown>): Promise<T> {
  const body = (await promise) as ApiBody<T>;
  if (body?.data === undefined) throw new Error(body?.message || 'Commercial lifecycle request failed');
  return body.data;
}

export type CommercialLifecycleGateResult = {
  gateKey: string;
  outcome: string;
  detail?: string;
};

export type CommercialLifecycleStageDef = {
  stageKey: string;
  sortOrder: number;
  entityType: string;
  isOptional: boolean;
  isSkippable: boolean;
  requiresApproval: boolean;
  successorEntityType: string | null;
  requiredPackKey: string | null;
  conversionProfileKey: string | null;
  gateKeys: string[];
  permissionCode: string | null;
};

export type CommercialLifecycleDefinition = {
  code: string;
  version: number;
  title: string;
  requiredPackKeys: string[];
  stages: CommercialLifecycleStageDef[];
};

export type CommercialLifecycleContext = {
  disabled: boolean;
  lifecycle: {
    status: string;
    definitionCode: string | null;
    title: string | null;
    installedPackKeys: string[];
    diagnostics: string[];
  };
  currentStage: {
    stageKey: string | null;
    entityType: string | null;
    sortOrder: number | null;
    requiresApproval: boolean;
    isOptional: boolean;
  } | null;
  lineage: {
    instanceId: string | null;
    rootEntityType: string | null;
    rootEntityId: string | null;
    entities: Array<{
      entityType: string;
      entityId: string;
      stageKey: string;
      revision: number;
    }>;
  };
  availableActions: Array<{
    action: string;
    toStageKey: string;
    conversionProfileKey: string | null;
    implementation: string | null;
  }>;
  blockedActions: Array<{
    action: string;
    toStageKey: string;
    reason: string;
  }>;
  nextStages: Array<{
    stageKey: string;
    entityType: string;
    isOptional: boolean;
    requiresApproval: boolean;
    conversionProfileKey: string | null;
  }>;
  gateResults: CommercialLifecycleGateResult[];
  progress: {
    completedStages: number;
    totalStages: number;
    percent: number;
  };
  relatedEntities: Array<{
    entityType: string;
    entityId: string;
    stageKey: string;
  }>;
};

export type CommercialLifecycleDiagnostics = {
  disabled?: boolean;
  status?: string;
  definitionCode?: string | null;
  installedPackKeys?: string[];
  diagnostics?: string[];
  [key: string]: unknown;
};

export const commercialLifecycleService = {
  getContext: (entityType: string, entityId: string) =>
    unwrap<CommercialLifecycleContext>(
      api.get('/commercial-lifecycle/context', {
        params: { entity_type: entityType, entity_id: entityId },
      })
    ),

  getDiagnostics: () =>
    unwrap<CommercialLifecycleDiagnostics>(api.get('/commercial-lifecycle/diagnostics')),

  getDefinitions: () =>
    unwrap<CommercialLifecycleDefinition[]>(api.get('/commercial-lifecycle/definitions')),
};
